import React, { useEffect, useMemo, useState } from "react";
import SlidePanel from "./SlidePanel";
import { parseCoords } from "../helpers/coordinates";
import { findSimilar } from "../helpers/similar";

const SHIP_TYPES = ["Cargo ship", "Tanker", "Passenger ship", "Ferry", "Fishing vessel", "Warship", "Submarine", "Tug", "Yacht", "Sailing ship", "Barge", "Other"];

const URL_RE = /^https?:\/\/\S+$/i;
const LICENSES = ["CC0 / Public domain", "CC BY 4.0", "CC BY-SA 4.0", "CC BY-NC 4.0", "Own work (CC BY-SA 4.0)", "Used with permission", "All rights reserved"];
export const splitList = (v) => String(v || "").split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

const NO_DUPES = []; // stable default, so the memo below isn't recomputed on every render

// the value, but only after it has stopped changing for `ms`
function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function SubmitWidget({
  submitData, setSubmitData, handleSubmitShip, onClose, submitting,
  pickMode, setPickMode, onUseMyLocation, hasDraft, onClearDraft,
  mode = "submit", original, shipTitle,
  ships = [], onShowShip, extraDuplicates = NO_DUPES, // for the "already on the map?" warning
}) {
  const editing = mode === "edit";
  const [showMore, setShowMore] = useState(mode === "edit");
  const set = (k) => (e) => setSubmitData({ ...submitData, [k]: e.target.value });
  // credit/licence are stored per photo URL so they stay attached if the list is reordered
  const setMeta = (url, field) => (e) => {
    const all = submitData.image_meta || {};
    setSubmitData({ ...submitData, image_meta: { ...all, [url]: { credit: "", license: "", ...all[url], [field]: e.target.value } } });
  };

  const coords = useMemo(() => parseCoords(submitData.coords), [submitData.coords]);
  const images = splitList(submitData.images);
  const links = splitList(submitData.links);
  const badImages = images.filter((u) => !URL_RE.test(u));
  const badLinks = links.filter((u) => !URL_RE.test(u));

  // Every photo needs a credit. Photos already on the ship (when editing) are grandfathered.
  const knownImages = useMemo(() => new Set(editing ? splitList(original?.images) : []), [editing, original]);
  const needsCredit = (u) => !knownImages.has(u) && !String((submitData.image_meta || {})[u]?.credit || "").trim();
  const uncredited = images.filter(needsCredit);

  // Duplicate check: ships already on the map (checked here, instantly) plus any the server found in the review queue.
  const dTitle = useDebounced(submitData.title, 350);
  const dImo = useDebounced(submitData.imo, 350);
  const dupes = useMemo(() => {
    if (editing) return [];
    const local = findSimilar(ships, { title: dTitle, lat: coords?.lat, lng: coords?.lng, imo: dImo });
    const seen = new Set(local.map((d) => d.id));
    return [...local, ...extraDuplicates.filter((d) => !seen.has(d.id))];
  }, [editing, ships, dTitle, dImo, coords, extraDuplicates]);
  const likelyKey = dupes.filter((d) => d.level === "likely").map((d) => d.id).join(",");
  const mustConfirm = likelyKey !== "";
  const [confirmedNew, setConfirmedNew] = useState(false);
  useEffect(() => setConfirmedNew(false), [likelyKey]); // a different match needs a fresh confirmation
  const dupeBlocked = mustConfirm && !confirmedNew;

  const titleOk = submitData.title.trim().length > 0;
  const dirty = !editing || JSON.stringify(submitData) !== JSON.stringify(original);
  const canSubmit = titleOk && !!coords && !badImages.length && !badLinks.length && !uncredited.length && !submitting && dirty && !dupeBlocked;
  const descLeft = 5000 - submitData.description.length;

  return (
    <SlidePanel
      title={editing ? "Suggest an Edit" : "Submit a Shipwreck"}
      subtitle={editing ? `Fix or add details for “${shipTitle}”` : "Only a name and a location are required"}
      onClose={onClose}
      wide
    >
      {editing && (
        <div className="draft-banner">
          Changes go to an admin for review and only appear on the map once approved.
        </div>
      )}
      {!editing && hasDraft && (
        <div className="draft-banner">
          Draft restored from your last visit.
          <button className="panel-link danger" onClick={onClearDraft}>Discard</button>
        </div>
      )}

      <div className="form-section">
        <label className="field">
          <span>Ship name <em>*</em></span>
          <input type="text" placeholder="e.g. SS Edmund Fitzgerald" value={submitData.title} onChange={set("title")} maxLength={120} autoFocus />
        </label>

        <div className="field">
          <span>Location <em>*</em></span>
          <input
            type="text"
            placeholder="Paste coordinates, e.g. 41.7325, -49.9469"
            value={submitData.coords}
            onChange={set("coords")}
            className={submitData.coords && !coords ? "invalid" : ""}
          />
          <div className="field-actions">
            <button type="button" className={`chip-btn${pickMode ? " active" : ""}`} onClick={() => setPickMode(!pickMode)}>
              📍 {pickMode ? "Tap the map…" : "Pick on map"}
            </button>
            <button type="button" className="chip-btn" onClick={onUseMyLocation}>◎ Use my location</button>
          </div>
          {submitData.coords && (
            <small className={coords ? "ok" : "bad"}>
              {coords ? `✓ ${coords.lat.toFixed(4)}°, ${coords.lng.toFixed(4)}° — drag the marker on the map to fine-tune` : "Couldn't read that. Try “lat, lng” or 41°43'57\"N 49°56'48\"W"}
            </small>
          )}
        </div>

        {dupes.length > 0 && (
          <div className={`dup-warning${mustConfirm ? " dup-likely" : ""}`} role="alert">
            <strong>{mustConfirm ? "This may already be on the map" : "Similar wrecks nearby"}</strong>
            <ul>
              {dupes.map((d) => (
                <li key={d.id}>
                  <span className="dup-title">{d.title}</span>
                  <small>{d.reasons.join(" · ")}{d.status === "pending" ? " · awaiting review" : ""}</small>
                  {d.status !== "pending" && onShowShip && (
                    <button type="button" className="panel-link" onClick={() => onShowShip(d)}>Show on map</button>
                  )}
                </li>
              ))}
            </ul>
            {mustConfirm && (
              <>
                <small>If it's the same wreck, open it and use <strong>✎ Suggest edit</strong> instead of adding it twice.</small>
                <label className="dup-confirm">
                  <input type="checkbox" checked={confirmedNew} onChange={(e) => setConfirmedNew(e.target.checked)} />
                  <span>I've checked: mine is a different wreck</span>
                </label>
              </>
            )}
          </div>
        )}

        <label className="field">
          <span>Description <small>(Markdown supported)</small></span>
          <textarea
            placeholder="What happened? When did it sink? Why is it notable?"
            value={submitData.description}
            onChange={set("description")}
            maxLength={5000}
          />
          <small className={descLeft < 200 ? "bad" : ""}>{descLeft} characters left</small>
        </label>

        <label className="field">
          <span>Photo links <small>(one per line — each photo needs an attribution below)</small></span>
          <textarea
            className="short"
            placeholder={"https://example.com/photo1.jpg\nhttps://example.com/photo2.jpg"}
            value={submitData.images}
            onChange={set("images")}
          />
          {badImages.length > 0 && <small className="bad">Each link must start with http:// or https://</small>}
          {images.length > 0 && !badImages.length && (
            <div className="img-previews">
              {images.slice(0, 6).map((u) => (
                <img key={u} src={u} alt="" onError={(e) => (e.currentTarget.style.opacity = 0.25)} />
              ))}
            </div>
          )}
        </label>

        <label className="field">
          <span>Photo attribution <small>(optional)</small></span>
          <input
            type="text"
            placeholder="e.g. Photo by Jane Doe, via Wikimedia Commons (CC BY-SA 4.0)"
            value={submitData.photo_attribution || ""}
            onChange={set("photo_attribution")}
            maxLength={300}
          />
          <small>Who took the photo(s) and where they came from. Saved with your submission and shown with the photos.</small>
        </label>

        {images.length > 0 && !badImages.length && (
          <div className="field image-credits">
            <span>Photo attribution <em>*</em> <small>(required for every photo: who took it, and what may others do with it?)</small></span>
            {images.map((u, i) => {
              const m = (submitData.image_meta || {})[u] || {};
              return (
                <div key={u} className="image-credit-row">
                  <img src={u} alt="" onError={(e) => (e.currentTarget.style.opacity = 0.25)} />
                  <div className="image-credit-inputs">
                    <small className="image-credit-label">Photo {i + 1}</small>
                    <input type="text" placeholder="Credit (required), e.g. Jane Doe / Wikimedia Commons" value={m.credit || ""} onChange={setMeta(u, "credit")} maxLength={200} required={needsCredit(u)} className={needsCredit(u) ? "invalid" : ""} aria-label={`Credit for photo ${i + 1} (required)`} aria-invalid={needsCredit(u)} />
                    <input type="text" list="photo-licenses" placeholder="License, e.g. CC BY-SA 4.0" value={m.license || ""} onChange={setMeta(u, "license")} maxLength={80} aria-label={`License for photo ${i + 1}`} />
                  </div>
                </div>
              );
            })}
            <datalist id="photo-licenses">{LICENSES.map((l) => <option key={l} value={l} />)}</datalist>
            <small className={uncredited.length ? "bad" : ""}>{uncredited.length ? `Add a credit for ${uncredited.length} more photo${uncredited.length === 1 ? "" : "s"} before submitting. ` : ""}Only submit photos you took or that are free to reuse. The credit is shown publicly with the photo; the license is optional but encouraged.</small>
          </div>
        )}

        <label className="field">
          <span>Source links <small>(one per line)</small></span>
          <textarea className="short" placeholder="Wikipedia, news articles, wreck diving reports…" value={submitData.links} onChange={set("links")} />
          {badLinks.length > 0 && <small className="bad">Each link must start with http:// or https://</small>}
        </label>
      </div>

      {editing && (
        <label className="field">
          <span>Why this change? <small>(optional, helps the reviewer)</small></span>
          <textarea className="short" placeholder="e.g. Correct date per the official inquiry report…" value={submitData.note || ""} onChange={set("note")} maxLength={500} />
        </label>
      )}

      <button type="button" className="more-toggle" onClick={() => setShowMore(!showMore)} aria-expanded={showMore}>
        {showMore ? "▾" : "▸"} Ship details <small>(optional)</small>
      </button>

      {showMore && (
        <div className="form-section form-grid">
          <label className="field"><span>Date of loss</span><input type="text" placeholder="e.g. 10 Nov 1975" value={submitData.date} onChange={set("date")} /></label>
          <label className="field"><span>IMO number</span><input type="text" inputMode="numeric" placeholder="7 digits" value={submitData.imo} onChange={set("imo")} /></label>
          <label className="field">
            <span>Type</span>
            <input type="text" list="ship-types" placeholder="Choose or type" value={submitData.type} onChange={set("type")} />
            <datalist id="ship-types">{SHIP_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
          </label>
          <label className="field"><span>Builder</span><input type="text" value={submitData.builder} onChange={set("builder")} /></label>
          <label className="field"><span>Owner</span><input type="text" value={submitData.owner} onChange={set("owner")} /></label>
          <label className="field"><span>Port of registry</span><input type="text" value={submitData.port_of_registry} onChange={set("port_of_registry")} /></label>
          <label className="field"><span>Length (m)</span><input type="text" inputMode="decimal" value={submitData.length} onChange={set("length")} /></label>
          <label className="field"><span>Beam (m)</span><input type="text" inputMode="decimal" value={submitData.beam} onChange={set("beam")} /></label>
          <label className="field"><span>Draught (m)</span><input type="text" inputMode="decimal" value={submitData.draught} onChange={set("draught")} /></label>
          <label className="field"><span>Tonnage</span><input type="text" value={submitData.tonnage} onChange={set("tonnage")} /></label>
          <label className="field"><span>Capacity</span><input type="text" value={submitData.capacity} onChange={set("capacity")} /></label>
          <label className="field"><span>Crew</span><input type="text" value={submitData.crew} onChange={set("crew")} /></label>
        </div>
      )}

      <p className="panel-hint">
        {editing
          ? "If approved, you'll be credited as an editor of this ship."
          : "Approved submissions are public and credited to your username. Your draft is saved in this browser until you submit."}
      </p>

      <div className="submit-bar">
        <button className="panel-primary" onClick={() => handleSubmitShip(coords, images, links, { confirmDuplicate: mustConfirm && confirmedNew })} disabled={!canSubmit}>
          {submitting ? "Submitting…" : editing ? "Submit changes for review" : "Submit for review"}
        </button>
        {!canSubmit && !submitting && (
          <small className="panel-muted">
            {!dirty ? "Change something to submit an edit" : !titleOk ? "Add a ship name" : !coords ? "Add a valid location" : uncredited.length ? "Add an attribution for each photo" : dupeBlocked ? "Confirm this is a different wreck" : "Fix the highlighted links"}
          </small>
        )}
      </div>
    </SlidePanel>
  );
}
